import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../layouts/layout";
import Modal from "../components/modal";
import Spinner from "../components/spinner";
import Loader from "../components/loader";
import WA_BOT from "../api/services/WA_BOT";
import SUBSCRIPTION from "../api/services/SUBSCRIPTION";
import styles from "../styles/_screens/whatsappBot.module.scss";

// Fallback only — the live price comes from GET /wa-bot/status (`price`), which
// a super-admin can change without a release.
const ADDON_PRICE_FALLBACK = 109;
const TRIAL_DAYS_FALLBACK = 7;
const ADDON_KEY = "whatsapp_bot";
// The structured מזכירת-AI form lives on its own screen; this path is
// hardcoded because routes/paths.js is owned by the integration step.
const AI_SETTINGS_PATH = "/whatsapp-bot/ai-settings";
const DEMO_VIDEO_URL =
  "https://tor.digital/wp-content/uploads/wa-assistant/wa-bot-demo.mp4";
// Sun..Sat — matches the backend/mobile day indexing (0-6).
const DAY_LABELS = ["א", "ב", "ג", "ד", "ה", "ו", "ש"];
// What each WhatsApp session state means to a business owner. WAHA's own
// vocabulary plus the two failure modes our side can report.
const HEALTH_LABELS = {
  WORKING: "מחובר ופעיל",
  SCAN_QR_CODE: "ממתין לסריקת QR — הבוט אינו עונה עד שתסרקו",
  STARTING: "מתחבר…",
  STOPPED: "החיבור נעצר — צריך לסרוק QR מחדש",
  FAILED: "החיבור נכשל — צריך לסרוק QR מחדש",
  unreachable: "שרת הוואטסאפ אינו מגיב",
  platform_unreachable: "לא הצלחנו להגיע לשרת שלנו",
  check_failed: "הבדיקה נכשלה",
  none: "לא חובר מספר וואטסאפ",
};
// A probe can fail without telling us anything about the business's WhatsApp
// line: our own request can die (network/500), and our server can fail to reach
// the bot platform. Neither means "the line is down" — rendering them as a
// disconnection is a false alarm that sends the owner to re-scan a QR for a
// connection that never broke, and teaches them to distrust the banner.
const HEALTH_INCONCLUSIVE = ["check_failed", "platform_unreachable"];
// States that ARE a verdict about the session itself. `unreachable` belongs
// here on purpose: it means the platform did ask WAHA and got nothing back,
// which is its own answer that the bot is not serving anyone.
const HEALTH_DEFINITE = [
  "WORKING",
  "SCAN_QR_CODE",
  "STARTING",
  "STOPPED",
  "FAILED",
  "unreachable",
  "none",
];
// True when the check produced no verdict — including a state we do not know,
// which must be explained rather than echoed back at the owner as an enum.
const healthInconclusive = (h) =>
  !!h &&
  !h.live &&
  (h.unknown === true ||
    HEALTH_INCONCLUSIVE.includes(h.wahaState) ||
    !HEALTH_DEFINITE.includes(h.wahaState));
const healthReason = (h) =>
  (h && HEALTH_LABELS[h.wahaState]) || "לא הצלחנו לקרוא את מצב החיבור";
// The platform keeps a numeric legacy tone next to the compiled directives.
// Mirror the structured choice into it so the two never contradict each other.
const LEGACY_TONE = { friendly: 1, concise: 3, professional: 4, premium: 4 };
const DEFAULT_EXISTING_ONLY_MSG = "העסק מקבל כרגע לקוחות קיימים בלבד 🙏";
// New-client policy (backend: Business.waBot.newClientPolicy) — copy mirrors the mobile screen.
const POLICIES = [
  {
    v: "open",
    label: "פתוח לכולם",
    desc: "הבוט קובע תורים לכל מי שכותב — לקוחות חדשים וקיימים כאחד.",
  },
  {
    v: "lead_only",
    label: "איסוף לידים בלבד",
    desc: "לקוח חדש לא מקבל תור מיידי — הפרטים נשלחים אליכם כליד ואתם חוזרים אליו לתיאום.",
  },
  {
    v: "existing_only",
    label: "לקוחות קיימים בלבד",
    desc: "רק מי שכבר לקוח שלכם יכול לקבוע דרך הבוט. לקוח חדש מוזמן להשאיר הודעה.",
  },
];

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

/**
 * The free text the owner wrote in the OLD flat AI card (now on its own
 * screen). `registrationInfo` was removed as a concept — identification and
 * registration are automatic — so its content is folded into the free
 * instructions instead of being dropped, and `directions` is skipped because
 * the address and the Waze link are sourced from the business automatically.
 * Only ever used for a business that has not saved the structured form yet:
 * once it has, the platform's ai.instructions holds the COMPILED block and
 * re-sending it would duplicate the whole prompt on every save.
 */
const legacyFreeText = (botAi) => {
  const ai = botAi || {};
  return [ai.registrationInfo, ai.instructions]
    .map((x) => (x || "").trim())
    .filter(Boolean)
    .join("\n\n");
};

/**
 * The PLAN allowance (bot bundled in Pro rather than bought as an add-on).
 * The backend that reports it is committed but not deployed, and the exact
 * field names differ between the entitlement service and the endpoints, so
 * read every plausible spelling and return null the moment it is not clearly
 * there — the caller hides the whole card rather than rendering "NaN מתוך —".
 */
const planSources = (statusData, statsData) =>
  [
    statusData && statusData.plan,
    statusData,
    statsData && statsData.plan,
    statsData,
  ].filter((o) => o && typeof o === "object");

const pickFrom = (sources, keys) => {
  for (const src of sources) {
    for (const k of keys) {
      if (src[k] !== undefined && src[k] !== null) return src[k];
    }
  }
  return undefined;
};

/**
 * Is the bot bundled in the package rather than billed as an add-on? Separate
 * from the allowance numbers on purpose: the flag decides what we tell the
 * owner about MONEY, and it must hold even on a backend that reports the flag
 * but not yet the counters. Strictly `=== true` — an absent field means "we do
 * not know", and "we do not know" must not read as "included".
 */
const readPlanIncluded = (statusData, statsData) =>
  pickFrom(planSources(statusData, statsData), [
    "includedInPlan",
    "planIncluded",
  ]) === true;

const readPlanAllowance = (statusData, statsData) => {
  if (!readPlanIncluded(statusData, statsData)) return null;
  const sources = planSources(statusData, statsData);
  const quota = num(
    pickFrom(sources, ["messageQuota", "monthlyMessageQuota", "quota"])
  );
  const used = num(
    pickFrom(sources, [
      "monthlyBotMessages",
      "messagesUsed",
      "usedMessages",
      "quotaUsed",
    ])
  );
  if (quota === null && used === null) return null;
  return { quota, used };
};

const sx = {
  linkCard: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    width: "100%",
    textAlign: "right",
    background: "#0b1b36",
    border: "none",
    borderRadius: 16,
    padding: "18px 24px",
    cursor: "pointer",
  },
  linkCardTitle: { display: "block", fontSize: 16, fontWeight: 700, color: "#fff" },
  linkCardSub: { display: "block", fontSize: 13, color: "#94a3b8", marginTop: 2 },
  linkCardChevron: { fontSize: 20, color: "#94a3b8" },
  // Three states, not two: connected, down, and "we could not tell". The third
  // one is deliberately neutral — a red alarm for our own outage is a lie.
  healthBox: (ok, unknown) => ({
    marginTop: 16,
    padding: "12px 14px",
    borderRadius: 12,
    background: unknown ? "#f8fafc" : ok ? "#f0fdf4" : "#fef2f2",
    border: `1px solid ${unknown ? "#e2e8f0" : ok ? "#bbf7d0" : "#fecaca"}`,
  }),
  healthTitle: (ok, unknown) => ({
    fontSize: 14,
    fontWeight: 700,
    color: unknown ? "#475569" : ok ? "#166534" : "#b91c1c",
  }),
  healthRetryBtn: {
    marginTop: 8,
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#de1468",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  healthSub: { fontSize: 13, lineHeight: 1.7, color: "#64748b", marginTop: 2 },
  trialBanner: {
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 12,
    padding: "14px 16px",
    marginBottom: 16,
  },
  trialTitle: { fontSize: 18, fontWeight: 800, color: "#166534" },
  trialSub: { fontSize: 14, color: "#15803d", marginTop: 2 },
  trialLine: { fontSize: 14, fontWeight: 700, color: "#c2410c", margin: "8px 0 0" },
  benefitLine: { fontSize: 14, fontWeight: 700, color: "#16a34a", margin: "8px 0 0" },
  demoBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
    padding: "8px 4px",
    border: "none",
    background: "transparent",
    color: "#de1468",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },
  demoVideo: { width: "100%", borderRadius: 12, background: "#000" },
  statsRow: { display: "flex", flexWrap: "wrap", gap: 12, marginTop: 12 },
  statBox: {
    flex: "1 1 130px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: "12px 14px",
    textAlign: "center",
  },
  statNum: { fontSize: 22, fontWeight: 800, color: "#1e293b", lineHeight: 1.2 },
  statLabel: { fontSize: 12, color: "#64748b", marginTop: 2 },
  meterTrack: {
    height: 8,
    borderRadius: 8,
    background: "#e2e8f0",
    overflow: "hidden",
    marginTop: 10,
  },
  meterFill: (pct, over) => ({
    height: "100%",
    width: `${pct}%`,
    background: over ? "#dc2626" : "#25d366",
  }),
};

const WhatsAppBot = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // {addOnActive, connected, whatsappState, connectedPhone, bot}
  const [addOnSince, setAddOnSince] = useState(null); // addedAt from consumption payload

  const [view, setView] = useState("status"); // 'status' | 'qr'
  const [qr, setQr] = useState(null); // { state, qrBase64 }
  const pollRef = useRef(null);

  // Live WhatsApp-session probe — distinct from `status`, which reports the
  // last state we recorded. A session can die quietly and leave that saying
  // "connected".
  const [health, setHealth] = useState(null);
  const [checking, setChecking] = useState(false);
  // Usage / plan allowance. Optional everywhere: the endpoint may be missing
  // and the plan fields are not deployed yet.
  const [stats, setStats] = useState(null);

  // Confirm modals
  const [activateOpen, setActivateOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  // Settings form
  const [botEnabled, setBotEnabled] = useState(true);
  const [days, setDays] = useState([0, 1, 2, 3, 4]);
  const [fromTime, setFromTime] = useState("09:00");
  const [toTime, setToTime] = useState("19:00");
  const [offMessage, setOffMessage] = useState(
    "אנחנו סגורים כרגע — נחזור אליך בשעות הפעילות 🙏"
  );
  // The AI personality/abilities form is NOT edited here any more — it lives on
  // the AI-receptionist screen and is the server's source of truth. These two
  // are carried only so a save from this screen preserves what is already
  // configured instead of flattening it (see saveSettings).
  const [aiSettings, setAiSettings] = useState(null);
  const [legacyAi, setLegacyAi] = useState(null);
  // new-client policy
  const [newClientPolicy, setNewClientPolicy] = useState("open");
  const [existingOnlyMessage, setExistingOnlyMessage] = useState(
    DEFAULT_EXISTING_ONLY_MSG
  );

  // ── data ──────────────────────────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    try {
      const res = await WA_BOT.get_status(); // get() returns the body directly
      const data = res?.data || {};
      setStatus(data);
      if (data.bot) {
        setBotEnabled(!!data.bot.enabled);
        const ah = data.bot.activeHours;
        if (ah) {
          if (Array.isArray(ah.days)) setDays(ah.days);
          if (ah.from) setFromTime(ah.from);
          if (ah.to) setToTime(ah.to);
          if (ah.offMessage) setOffMessage(ah.offMessage);
        }
      }
      // Structured AI form (source of truth) + whatever the platform still
      // holds from the old flat form, for the one-time fold on save.
      setAiSettings(data.aiSettings || null);
      setLegacyAi((data.bot && data.bot.ai) || null);
      if (data.newClientPolicy) setNewClientPolicy(data.newClientPolicy);
      if (data.existingOnlyMessage)
        setExistingOnlyMessage(data.existingOnlyMessage);
      return data;
    } catch (e) {
      toast.error("שגיאה בטעינת סטטוס הבוט");
      return null;
    }
  }, []);

  // Usage — optional. A backend without the endpoint (or without the plan
  // fields on it) simply leaves the card hidden.
  const fetchStats = useCallback(async () => {
    try {
      const res = await WA_BOT.stats(30);
      setStats((res && res.data) || null);
    } catch (_) {
      setStats(null);
    }
  }, []);

  // Billing visibility — the add-on's "פעיל מאז <date>" comes from the same
  // consumption payload the subscription screen uses (planConsumptionInfo).
  const fetchAddOnDate = useCallback(async () => {
    try {
      const res = await SUBSCRIPTION.get_sub_details();
      const addOns = res?.data?.addOns || [];
      const wa = addOns.find((a) => a && a.key === ADDON_KEY);
      setAddOnSince(wa?.addedAt || null);
    } catch (_) {
      /* non-fatal — the billing line just omits the date */
    }
  }, []);

  // Live connection probe. Silent on load/focus (the banner should not be
  // guessing), loud when the owner presses the button.
  const checkConnection = useCallback(async ({ silent } = {}) => {
    if (!silent) setChecking(true);
    try {
      const res = await WA_BOT.health(); // get() returns the body directly
      const data = (res && res.data) || null;
      setHealth(data);
      return data;
    } catch (e) {
      // Our request died. That is a fact about US, not about the business's
      // WhatsApp — flag it as inconclusive so nothing downstream reports a
      // disconnection we never observed.
      const failed = {
        live: false,
        unknown: true,
        wahaState: "check_failed",
        reachable: false,
      };
      setHealth(failed);
      return failed;
    } finally {
      if (!silent) setChecking(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const [data] = await Promise.all([
        fetchStatus(),
        fetchAddOnDate(),
        fetchStats(),
      ]);
      // Only probe when there is something to probe — with no add-on or no
      // linked number the endpoint just answers "not entitled".
      if (data && data.addOnActive && data.connected) {
        checkConnection({ silent: true });
      }
      setLoading(false);
    })();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [fetchStatus, fetchAddOnDate, fetchStats, checkConnection]);

  // Re-check when the tab regains focus: a WhatsApp session can drop while the
  // owner is away (phone off, linked device removed), and the add-on itself may
  // have been cancelled in another tab or from the app.
  useEffect(() => {
    const onFocus = async () => {
      const data = await fetchStatus();
      if (data && data.addOnActive && data.connected) {
        checkConnection({ silent: true });
      } else {
        setHealth(null);
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchStatus, checkConnection]);

  // ── QR polling (every 3s, auto-advance on working) ──
  const startQrPolling = useCallback(() => {
    setView("qr");
    setQr(null);
    const tick = async () => {
      try {
        const j = await WA_BOT.get_qr(); // { state, qrBase64 }
        setQr(j);
        if (j?.state === "working") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          await fetchStatus();
          setView("status");
          toast.success("הוואטסאפ של העסק מחובר והבוט פעיל ✅");
        }
      } catch (e) {
        /* keep polling — 409 until the platform is ready */
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  }, [fetchStatus]);

  const stopQrPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setView("status");
  };

  // "בדיקת חיבור והפקת QR" — probe the session and, when it is DOWN, go
  // straight into the scan flow instead of leaving the owner to find it.
  // When the probe itself failed we know nothing, so we say nothing and stay
  // put: re-linking the device over our own outage fixes nothing and reads as
  // "your WhatsApp broke" when it did not. The banner keeps a manual way in.
  const recheckConnection = useCallback(async () => {
    const data = await checkConnection();
    if (data && data.live) {
      toast.success("הבוט מחובר וחי מול השרת ✅");
      return;
    }
    if (healthInconclusive(data)) {
      toast.error(
        `${healthReason(data)} — לא הצלחנו לבדוק את החיבור. ייתכן שהוא תקין. נסו שוב בעוד רגע.`
      );
      return;
    }
    toast.error(
      `${
        (data && HEALTH_LABELS[data.wahaState]) || "החיבור לוואטסאפ אינו פעיל"
      } — מפיקים קוד QR חדש לסריקה`
    );
    startQrPolling();
  }, [checkConnection, startQrPolling]);

  // ── actions ──────────────────────────────────────────────────────────────
  const doConnect = useCallback(async () => {
    setBusy(true);
    try {
      const res = await WA_BOT.connect({});
      const body = res?.data || {};
      if (body.status === 200 || body.data) {
        await fetchStatus();
        startQrPolling();
      } else {
        toast.error(body?.message || "שגיאה בחיבור הבוט");
      }
    } catch (e) {
      // connect can 502 locally when the WhatsApp platform creds are missing —
      // surface the server message and stay on the connect card.
      toast.error(
        e?.response?.data?.message ||
          "שגיאה בחיבור לפלטפורמת הוואטסאפ. נסו שוב בעוד רגע."
      );
    } finally {
      setBusy(false);
    }
  }, [fetchStatus, startQrPolling]);

  const confirmActivate = useCallback(async () => {
    setBusy(true);
    try {
      const res = await WA_BOT.activate_addon();
      if (res?.data?.status === 200) {
        setActivateOpen(false);
        // A plan that already bundles the receptionist is never charged for it —
        // the server says so instead of creating a billable add-on, so tell the
        // owner rather than silently doing nothing.
        if (res?.data?.data?.includedInPlan) {
          toast.success(res?.data?.message || "המזכירה כלולה בחבילה שלך");
        }
        await Promise.all([fetchStatus(), fetchAddOnDate(), fetchStats()]);
        // straight into the connect + QR flow
        doConnect();
      } else {
        toast.error(res?.data?.message || "שגיאה בהוספת התוסף");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "שגיאה בהוספת התוסף");
    } finally {
      setBusy(false);
    }
  }, [fetchStatus, fetchAddOnDate, fetchStats, doConnect]);

  const confirmRemove = useCallback(async () => {
    setBusy(true);
    try {
      await WA_BOT.remove_addon();
      setRemoveOpen(false);
      setAddOnSince(null);
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.message || "שגיאה בהסרת התוסף");
    } finally {
      setBusy(false);
    }
  }, [fetchStatus]);

  const saveSettings = useCallback(async () => {
    setBusy(true);
    try {
      // One payload — send enabled + activeHours + the FULL ai block together
      // (§6.5: always send all ai fields so none is dropped from the KB block).
      // newClientPolicy/existingOnlyMessage are OUR top-level fields (persisted
      // on Business.waBot) — NOT part of the platform ai block.
      //
      // The receptionist's personality now lives in Business.waBot.aiSettings,
      // which the server recompiles into ai.instructions on EVERY save. This
      // screen therefore sends no prompt text of its own — that is exactly what
      // used to silently override the settings configured in the mobile app:
      //   • directions       — dropped: address + Waze link are auto-sourced
      //   • registrationInfo — dropped: identification/registration is automatic
      //   • instructions     — empty once a structured form exists (the server
      //     writes the compiled block there). Until then the owner's old free
      //     text is carried through, so nothing is lost before they migrate.
      const structured = !!aiSettings;
      const structuredTone =
        (aiSettings && aiSettings.personality && aiSettings.personality.tone) ||
        null;
      const res = await WA_BOT.save_settings({
        enabled: botEnabled,
        activeHours: { days, from: fromTime, to: toTime, offMessage },
        ai: {
          aiEnabled: botEnabled,
          tone:
            LEGACY_TONE[structuredTone] || num(legacyAi && legacyAi.tone) || 2,
          directions: "",
          registrationInfo: "",
          instructions: structured ? "" : legacyFreeText(legacyAi),
          handoffConditionText: structured
            ? ""
            : (legacyAi && legacyAi.handoffConditionText) || "",
        },
        newClientPolicy,
        existingOnlyMessage:
          (existingOnlyMessage || "").trim() || DEFAULT_EXISTING_ONLY_MSG,
      });
      if (res?.data?.status === 200) {
        toast.success("ההגדרות נשמרו בהצלחה");
      } else {
        toast.error(res?.data?.message || "שגיאה בשמירת ההגדרות");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "שגיאה בשמירת ההגדרות");
    } finally {
      setBusy(false);
    }
  }, [
    botEnabled,
    days,
    fromTime,
    toTime,
    offMessage,
    aiSettings,
    legacyAi,
    newClientPolicy,
    existingOnlyMessage,
  ]);

  // Copy a short-lived scan link for another device (desktop scans directly,
  // but a phone/tablet can open this to scan the code from a second screen).
  const shareScanLink = useCallback(async () => {
    setBusy(true);
    try {
      const res = await WA_BOT.share_link();
      const data = res?.data?.data;
      if (data?.url) {
        try {
          await navigator.clipboard.writeText(data.url);
          toast.success(
            `קישור סריקה הועתק (בתוקף ${data.expiresInMinutes} דקות) — פתחו אותו במכשיר אחר`
          );
        } catch (_) {
          // clipboard blocked — open the link so the user can copy it manually
          window.open(data.url, "_blank", "noopener,noreferrer");
        }
      } else {
        toast.error(res?.data?.message || "שגיאה ביצירת קישור");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "שגיאה ביצירת קישור");
    } finally {
      setBusy(false);
    }
  }, []);

  const toggleDay = (d) =>
    setDays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()
    );

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("he-IL");
    } catch (_) {
      return "";
    }
  };

  // ── explanation header (always visible) ──
  const Explanation = () => (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardTitleGroup}>
          <div className={styles.waIcon}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.86 9.86 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.2 8.2 0 0 1 2.41 5.83c0 4.54-3.7 8.24-8.24 8.24Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.48-.41-.42-.56-.43-.14-.01-.31-.01-.48-.01-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29Z" />
            </svg>
          </div>
          <span className={styles.cardTitle}>בוט וואטסאפ לעסק</span>
        </div>
      </div>
      <p className={styles.body}>
        בוט חכם שעונה ללקוחות ישירות מהוואטסאפ של העסק — 24/7. הבוט קובע תורים לפי
        זמינות אמיתית ביומן, מציג ללקוח את התורים הקרובים שלו, ומבטל או מזיז תורים —
        הכול אוטומטית, בעברית.
      </p>
      <p className={styles.body}>
        החיבור פשוט: מחברים את מספר הוואטסאפ של העסק בסריקת קוד QR חד-פעמית (כמו
        וואטסאפ ווב). מרגע החיבור הבוט חי ועונה בשעות שתגדירו.
      </p>
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.wrapper}>
            <div className={styles.content}>
              <Loader />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const addOnActive = status?.addOnActive;
  const connected = status?.connected;
  const activeNow = status?.bot?.activeNow;
  const working = qr?.state === "working";
  // The probe answered "I don't know" (our request failed, our server could not
  // reach the platform, or the state is one we do not recognise).
  const healthUnknown = healthInconclusive(health);

  // Pricing comes from the server (super-admin editable); the constants are
  // only a fallback for an older backend that does not send them.
  const price = num(status?.price) > 0 ? num(status.price) : ADDON_PRICE_FALLBACK;
  // What an already-subscribed business is actually billed — the price
  // snapshotted at activation, which stays put when the list price changes.
  const subscribedPrice = num(status?.subscribedPrice) > 0 ? num(status.subscribedPrice) : null;
  const trialDays = num(status?.trialDays) > 0 ? num(status.trialDays) : TRIAL_DAYS_FALLBACK;
  // A trial that has already ended is not a trial: the business is being billed
  // from that moment on. Clamping the countdown to a minimum of one day is only
  // meant to round "ends in a few hours" up to "1 יום" — applied to a date in
  // the past it kept telling a paying subscriber they were still free of
  // charge, forever. An unparseable date lands here too (NaN is not > 0).
  const trialMsLeft = status?.trialEndsAt
    ? new Date(status.trialEndsAt).getTime() - Date.now()
    : null;
  const trialDaysLeft =
    trialMsLeft !== null && trialMsLeft > 0
      ? Math.max(1, Math.ceil(trialMsLeft / 86400000))
      : null;

  // ── usage (last 30 days) — every field optional ──
  const messagesHandled = num(stats?.messagesHandled ?? stats?.messages);
  const usageTiles = stats
    ? [
        { label: "הודעות שטופלו", value: messagesHandled },
        { label: "שיחות", value: num(stats.conversations) },
        { label: "תורים נקבעו", value: num(stats.booked) },
        { label: "בוטלו", value: num(stats.cancelled) },
        { label: "לידים", value: num(stats.leads) },
      ].filter((t) => t.value !== null)
    : [];
  // Plan allowance (bot bundled in the package instead of bought as an add-on).
  // Null until the backend that reports it is live — then the card stays hidden.
  const allowance = readPlanAllowance(status, stats);
  // The money question, answered even when the counters are missing: a bundled
  // bot must never be shown a monthly charge, and an add-on must never be told
  // it is free.
  const planIncluded = readPlanIncluded(status, stats);
  // quota 0 means unlimited; quota null means the backend did not say, and a
  // missing number must never be phrased as "unlimited".
  const allowanceUnlimited =
    !!allowance && allowance.quota !== null && allowance.quota <= 0;
  const allowanceMetered =
    !!allowance && allowance.quota > 0 && allowance.used !== null;
  const allowancePct = allowanceMetered
    ? Math.min(100, Math.round((allowance.used / allowance.quota) * 100))
    : 0;
  const allowanceText = !allowance
    ? ""
    : allowanceUnlimited
    ? " ללא הגבלת הודעות."
    : allowanceMetered
    ? ` נוצלו ${allowance.used} מתוך ${allowance.quota} הודעות בחודש הנוכחי.`
    : allowance.quota !== null
    ? ` מכסה חודשית: ${allowance.quota} הודעות.`
    : allowance.used !== null
    ? ` נוצלו ${allowance.used} הודעות בחודש הנוכחי.`
    : "";

  // ── QR view ──
  if (view === "qr") {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.wrapper}>
            <div className={styles.content}>
              <h1 className={styles.pageTitle}>חיבור וואטסאפ</h1>
              <div className={styles.card}>
                <span className={styles.cardTitle}>סריקת קוד לחיבור הוואטסאפ</span>
                <p className={styles.body}>
                  בטלפון של העסק: וואטסאפ ← הגדרות ← מכשירים מקושרים ← קישור מכשיר,
                  וסרקו את הקוד שמופיע כאן על המסך.
                </p>
                <div className={styles.qrWrap}>
                  <div className={styles.qrBox}>
                    {working ? (
                      <span className={styles.qrDone}>✅</span>
                    ) : qr?.qrBase64 ? (
                      <img
                        className={styles.qrImg}
                        alt="QR"
                        src={`data:image/png;base64,${qr.qrBase64}`}
                      />
                    ) : (
                      <Spinner />
                    )}
                  </div>
                  <div className={styles.qrState}>
                    {working
                      ? "מחובר! הבוט פעיל."
                      : qr?.state === "qr"
                      ? "ממתין לסריקה…"
                      : "טוען קוד…"}
                  </div>
                </div>
                <div className={styles.tipBox}>
                  לא ניתן לסרוק מאותו טלפון שמציג את הקוד. במחשב — סרקו ישירות
                  מהמסך. לחלופין, העתיקו קישור סריקה ופתחו אותו במכשיר אחר.
                </div>
                <div className={styles.qrActions}>
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={shareScanLink}
                    disabled={busy}
                  >
                    {busy ? <Spinner /> : "פתח קישור סריקה במכשיר אחר"}
                  </button>
                  <button
                    type="button"
                    className={styles.ghostBtn}
                    onClick={stopQrPolling}
                  >
                    חזרה
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.content}>
            <h1 className={styles.pageTitle}>בוט וואטסאפ לעסק</h1>

            <Explanation />

            {/* Billing visibility — compact active-addon line */}
            {addOnActive && (
              <div className={styles.billingLine}>
                <span className={styles.billingDot} />
                <span>
                  {planIncluded
                    ? "התוסף פעיל — כלול בחבילה שלך"
                    : `התוסף פעיל — ₪${subscribedPrice || price}/חודש`}
                  {addOnSince ? ` · פעיל מאז ${formatDate(addOnSince)}` : ""}
                  {planIncluded
                    ? " · ללא חיוב נוסף"
                    : " · מתווסף לחשבונית החודשית שלך"}
                </span>
              </div>
            )}

            {/* ── NOT ACTIVATED: trial-first activation ── */}
            {!addOnActive && (
              <div className={styles.card}>
                {/* The trial leads, because it is the actual offer — the price
                    is what happens after it, not what you pay to start. */}
                <div style={sx.trialBanner}>
                  <div style={sx.trialTitle}>
                    ניסיון ללא עלות ל-{trialDays} ימים
                  </div>
                  <div style={sx.trialSub}>
                    המזכירה עונה ללקוחות שלכם מהיום הראשון — בלי לשלם שקל.
                  </div>
                </div>
                <div className={styles.priceRow}>
                  <span className={styles.price}>₪{price}</span>
                  <span className={styles.priceUnit}> / חודש לאחר הניסיון</span>
                </div>
                <p className={styles.body}>
                  בתום {trialDays} הימים התוסף יתווסף לחשבונית החודשית שלך כשורה
                  נפרדת, בנוסף לחבילה הקיימת. ללא התחייבות — אפשר להסיר בכל עת,
                  וההסרה מפסיקה את החיוב מהחודש הבא.
                </p>
                <ul className={styles.bullets}>
                  {[
                    "קביעת תורים לפי זמינות אמיתית",
                    "הצגת התורים הקרובים של הלקוח",
                    "ביטול ושינוי תור אוטומטיים",
                    "מענה בשעות שתגדירו + העברה לנציג",
                  ].map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={() => setActivateOpen(true)}
                  disabled={busy}
                >
                  {busy ? <Spinner /> : `התחלת ניסיון ללא עלות ל-${trialDays} ימים`}
                </button>
                <div>
                  <button type="button" style={sx.demoBtn} onClick={() => setDemoOpen(true)}>
                    ▶ צפייה בדוגמה
                  </button>
                </div>
              </div>
            )}

            {/* ── ACTIVATED but not connected: connect ── */}
            {addOnActive && !connected && (
              <div className={styles.card}>
                <span className={styles.cardTitle}>חיבור הוואטסאפ</span>
                <p className={styles.body}>
                  התוסף פעיל 🎉 כעת נחבר את מספר הוואטסאפ של העסק כדי שהבוט יתחיל
                  לענות.
                </p>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={doConnect}
                  disabled={busy}
                >
                  {busy ? <Spinner /> : "חיבור עכשיו"}
                </button>
                <button
                  type="button"
                  className={styles.removeLink}
                  onClick={() => setRemoveOpen(true)}
                >
                  הסרת התוסף
                </button>
              </div>
            )}

            {/* ── ACTIVATED + connected: status + settings ── */}
            {addOnActive && connected && (
              <>
                {/* The structured מזכירת-AI form — personality, abilities,
                    knowledge base, goal. Its own screen, shared with the app. */}
                <button
                  type="button"
                  style={sx.linkCard}
                  onClick={() => navigate(AI_SETTINGS_PATH)}
                >
                  <span style={{ fontSize: 22 }}>👩‍💼</span>
                  <span style={{ flex: 1 }}>
                    <span style={sx.linkCardTitle}>הגדרות מזכירת AI</span>
                    <span style={sx.linkCardSub}>
                      אישיות, הרשאות ביומן, מאגר ידע, העברה לנציג ויעדים
                    </span>
                  </span>
                  <span style={sx.linkCardChevron}>‹</span>
                </button>

                <div className={styles.card}>
                  <div className={styles.statusRow}>
                    <span
                      className={styles.statusDot}
                      style={{ background: activeNow ? "#16a34a" : "#f59e0b" }}
                    />
                    <span className={styles.statusText}>
                      {activeNow ? "פעיל עכשיו" : "מחובר · מחוץ לשעות הפעילות"}
                    </span>
                  </div>
                  {!!status?.connectedPhone && (
                    <p className={styles.phone}>
                      מספר מחובר: {status.connectedPhone}
                    </p>
                  )}
                  {/* Trial / benefit / paid state */}
                  {status?.grantedFree ? (
                    <p style={sx.benefitLine}>🎁 הטבה פעילה — ללא עלות</p>
                  ) : trialDaysLeft ? (
                    <p style={sx.trialLine}>
                      {`בתקופת ניסיון ללא עלות — נותרו ${trialDaysLeft} ימים · לאחר מכן ₪${
                        subscribedPrice || price
                      }/חודש`}
                    </p>
                  ) : null}
                  {/* Live connection banner. Distinct from "פעיל עכשיו" above,
                      which is about working HOURS — this is about whether
                      WhatsApp is actually attached. */}
                  {!!health && (
                    <div style={sx.healthBox(!!health.live, healthUnknown)}>
                      <div style={sx.healthTitle(!!health.live, healthUnknown)}>
                        {health.live
                          ? "✅ מחובר לוואטסאפ וחי מול השרת"
                          : healthUnknown
                          ? "לא הצלחנו לבדוק את החיבור כרגע"
                          : "⚠️ הבוט אינו מחובר"}
                      </div>
                      <div style={sx.healthSub}>
                        {healthUnknown
                          ? `${healthReason(health)} — ייתכן שהחיבור תקין. נסו שוב בעוד רגע.`
                          : healthReason(health)}
                        {health.checkedAt
                          ? ` · נבדק ב-${new Date(health.checkedAt).toLocaleTimeString(
                              "he-IL",
                              { hour: "2-digit", minute: "2-digit" }
                            )}`
                          : ""}
                      </div>
                      {/* The old behaviour dropped the owner into the scan flow
                          on any failure. Now that a failed CHECK no longer does
                          that, keep the door open for someone who is sure the
                          line is down. */}
                      {healthUnknown && (
                        <button
                          type="button"
                          style={sx.healthRetryBtn}
                          onClick={startQrPolling}
                        >
                          בכל זאת להפיק קוד QR לסריקה
                        </button>
                      )}
                    </div>
                  )}
                  <div className={styles.toggleRow}>
                    <span className={styles.toggleLabel}>הבוט פעיל</span>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        checked={botEnabled}
                        onChange={(e) => setBotEnabled(e.target.checked)}
                      />
                      <span className={styles.slider} />
                    </label>
                  </div>
                  <div className={styles.qrActions}>
                    <button
                      type="button"
                      className={styles.secondaryBtn}
                      onClick={recheckConnection}
                      disabled={checking}
                    >
                      {checking ? <Spinner /> : "בדיקת חיבור והפקת QR"}
                    </button>
                    <button
                      type="button"
                      className={styles.ghostBtn}
                      onClick={shareScanLink}
                      disabled={busy}
                    >
                      {busy ? <Spinner /> : "פתח קישור סריקה במכשיר אחר"}
                    </button>
                  </div>
                </div>

                {/* Usage — hidden entirely while the backend that reports it is
                    not live (no numbers is better than "NaN מתוך undefined"). */}
                {(usageTiles.length > 0 || allowance) && (
                  <div className={styles.card}>
                    <span className={styles.cardTitle}>
                      שימוש ב-{num(stats?.days) || 30} הימים האחרונים
                    </span>
                    {usageTiles.length > 0 && (
                      <div style={sx.statsRow}>
                        {usageTiles.map((t) => (
                          <div key={t.label} style={sx.statBox}>
                            <div style={sx.statNum}>{t.value}</div>
                            <div style={sx.statLabel}>{t.label}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {allowance && (
                      <>
                        <p className={styles.body}>
                          המזכירה כלולה בחבילה שלך — ללא חיוב נוסף.
                          {allowanceText}
                        </p>
                        {allowanceMetered && (
                          <div style={sx.meterTrack}>
                            <div
                              style={sx.meterFill(
                                allowancePct,
                                allowance.used >= allowance.quota
                              )}
                            />
                          </div>
                        )}
                        {allowanceMetered && allowance.used >= allowance.quota && (
                          <p className={styles.body}>
                            המכסה החודשית נוצלה — המזכירה תחזור לענות בתחילת החודש
                            הבא, או בשדרוג החבילה.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* active hours */}
                <div className={styles.card}>
                  <span className={styles.cardTitle}>שעות פעילות</span>
                  <div className={styles.daysRow}>
                    {DAY_LABELS.map((lbl, d) => {
                      const on = days.includes(d);
                      return (
                        <button
                          type="button"
                          key={d}
                          onClick={() => toggleDay(d)}
                          className={`${styles.dayChip} ${on ? styles.dayChipOn : ""}`}
                        >
                          {lbl}
                        </button>
                      );
                    })}
                  </div>
                  <div className={styles.timeRow}>
                    <div className={styles.timeBox}>
                      <label>משעה</label>
                      <input
                        type="time"
                        value={fromTime}
                        onChange={(e) => setFromTime(e.target.value)}
                      />
                    </div>
                    <div className={styles.timeBox}>
                      <label>עד שעה</label>
                      <input
                        type="time"
                        value={toTime}
                        onChange={(e) => setToTime(e.target.value)}
                      />
                    </div>
                  </div>
                  <label className={styles.fieldLabel}>הודעה מחוץ לשעות</label>
                  <textarea
                    className={styles.input}
                    value={offMessage}
                    onChange={(e) => setOffMessage(e.target.value)}
                    placeholder="למשל: אנחנו סגורים כרגע…"
                    rows={2}
                  />
                </div>

                {/* The flat AI form that used to sit here (טון, הוראות הגעה,
                    מידע על הרשמה, הנחיות, העברה לנציג) is gone: it wrote the
                    legacy platform fields directly and overwrote whatever the
                    owner had configured in the app. Everything it did — and a
                    great deal more — lives on the AI-receptionist screen linked
                    at the top of this page. */}

                {/* new-client policy */}
                <div className={styles.card}>
                  <span className={styles.cardTitle}>לקוחות חדשים</span>
                  <p className={styles.body}>
                    בחרו כיצד הבוט מתייחס לפונה שעדיין אינו לקוח של העסק.
                  </p>
                  <div className={styles.policyList}>
                    {POLICIES.map((p) => {
                      const on = newClientPolicy === p.v;
                      return (
                        <label
                          key={p.v}
                          className={`${styles.policyRow} ${on ? styles.policyRowOn : ""}`}
                        >
                          <input
                            type="radio"
                            name="newClientPolicy"
                            value={p.v}
                            checked={on}
                            onChange={() => setNewClientPolicy(p.v)}
                          />
                          <span className={styles.radio} />
                          <span className={styles.policyTexts}>
                            <span className={styles.policyLabel}>{p.label}</span>
                            <span className={styles.policyDesc}>{p.desc}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {newClientPolicy === "existing_only" && (
                    <div>
                      <label className={styles.fieldLabel}>
                        הודעה ללקוח חדש
                      </label>
                      <textarea
                        className={styles.input}
                        value={existingOnlyMessage}
                        onChange={(e) => setExistingOnlyMessage(e.target.value)}
                        placeholder={DEFAULT_EXISTING_ONLY_MSG}
                        rows={2}
                      />
                    </div>
                  )}
                </div>

                <div className={styles.saveRow}>
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    onClick={saveSettings}
                    disabled={busy}
                  >
                    {busy ? <Spinner /> : "שמירת הגדרות הבוט"}
                  </button>
                  <button
                    type="button"
                    className={styles.removeLink}
                    onClick={() => setRemoveOpen(true)}
                  >
                    {planIncluded
                      ? "הסרת התוסף"
                      : `הסרת התוסף (₪${subscribedPrice || price}/חודש)`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ACTIVATE CONFIRM — explicit trial consent: the trial is live from day
          one and becomes billable unless it is cancelled before it ends. */}
      {activateOpen && (
        <Modal setIsOpen={setActivateOpen} onClick={() => null}>
          <div className={styles.confirm}>
            <h2>ניסיון ללא עלות ל-{trialDays} ימים</h2>
            <p>
              המזכירה תתחיל לעבוד מיד, ללא עלות, למשך {trialDays} ימים. אם לא
              תבטלו עד תום הניסיון, התוסף יתווסף אוטומטית לחשבונית החודשית שלך
              בעלות ₪{price} לחודש (בנוסף לחבילה הקיימת), ויחזור מדי חודש עד
              שתסירו אותו. ההסרה זמינה כאן במסך זה בכל רגע.
            </p>
            <div className={styles.confirmBtns}>
              <button
                className={styles.confirmPrimary}
                onClick={confirmActivate}
                disabled={busy}
              >
                {busy ? <Spinner /> : `אישור והתחלת ניסיון · ${trialDays} ימים חינם`}
              </button>
              <button
                className={styles.confirmCancel}
                onClick={() => setActivateOpen(false)}
              >
                ביטול
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* DEMO — a short clip of a real conversation, before any commitment. */}
      {demoOpen && (
        <Modal setIsOpen={setDemoOpen} onClick={() => null} type={3}>
          <div className={styles.confirm}>
            <h2>כך המזכירה עובדת</h2>
            {/* Plain HTML5 video on purpose — no player dependency. */}
            <video
              style={sx.demoVideo}
              src={DEMO_VIDEO_URL}
              controls
              playsInline
              preload="metadata"
            />
            <div className={styles.confirmBtns} style={{ marginTop: 16 }}>
              <button
                className={styles.confirmCancel}
                onClick={() => setDemoOpen(false)}
              >
                סגירה
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* REMOVE CONFIRM — deactivates immediately + drops from next invoice */}
      {removeOpen && (
        <Modal setIsOpen={setRemoveOpen} onClick={() => null}>
          <div className={styles.confirm}>
            <h2>הסרת התוסף</h2>
            <p>
              הבוט יושבת מיד והחיוב לא ייכלל בחשבונית הבאה. להסיר את בוט הוואטסאפ?
            </p>
            <div className={styles.confirmBtns}>
              <button
                className={styles.confirmDanger}
                onClick={confirmRemove}
                disabled={busy}
              >
                {busy ? <Spinner /> : "הסרה"}
              </button>
              <button
                className={styles.confirmCancel}
                onClick={() => setRemoveOpen(false)}
              >
                ביטול
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default WhatsAppBot;
